/**
 * Machine-level kiosk override helpers — pure, so the save path can be unit-tested
 * without the store (Jest cannot load the store because of import.meta in api-config).
 */

import type { PreAuthSettings } from '../types/kiosk-settings-types';
import { sanitizePreAuth } from './pre-auth-validation';

/**
 * Removes from `overrides` any field whose value already matches the operator
 * default — only genuine kiosk-specific differences are kept.
 * Works recursively for nested objects.
 */
export function stripOperatorDefaults(
  overrides: Record<string, unknown>,
  operatorSettings: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(overrides)) {
    const kv = overrides[key];
    const ov = operatorSettings[key];

    if (
      kv !== null &&
      typeof kv === 'object' &&
      !Array.isArray(kv) &&
      ov !== null &&
      typeof ov === 'object' &&
      !Array.isArray(ov)
    ) {
      const nested = stripOperatorDefaults(
        kv as Record<string, unknown>,
        ov as Record<string, unknown>
      );
      if (Object.keys(nested).length > 0) {
        result[key] = nested;
      }
    } else if (JSON.stringify(kv) !== JSON.stringify(ov)) {
      // Note: deliberately not coalescing `ov` to null when undefined — an explicit
      // kiosk override of `null` (a genuine "clear this field") must be kept even when
      // the operator has no value for the field at all, otherwise the clear signal is
      // lost and the backend never learns the field should be unset.
      result[key] = kv;
    }
  }
  return result;
}

/**
 * Final machine override payload: strips fields equal to the operator default and
 * keeps preAuth as a whole object. preAuth is sanitized before the comparison (so the
 * 4-slot padding can't create a false diff) and written in full afterwards, because
 * stripOperatorDefaults may leave only the changed sub-field (e.g. just `enabled`).
 */
export function stripKioskOverrides(
  overrides: Record<string, unknown>,
  operatorSettings: Record<string, unknown>,
  preAuth: PreAuthSettings | undefined
): Record<string, unknown> {
  const withPreAuth = { ...overrides };
  if ('preAuth' in withPreAuth) {
    withPreAuth['preAuth'] = sanitizePreAuth(preAuth);
  }

  const payload = Object.keys(operatorSettings).length > 0
    ? stripOperatorDefaults(withPreAuth, operatorSettings)
    : withPreAuth;

  if ('preAuth' in payload) {
    payload['preAuth'] = sanitizePreAuth(preAuth);
  }
  return payload;
}
